import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { RoleBadge } from "@/components/RoleBadge";
import { LogInAsButton } from "@/components/LogInAsButton";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, Mail, Send, KeyRound } from "lucide-react";

export default function AdminsPage() {
    const { user } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ email: "", full_name: "", role: "admin" });
    const [formError, setFormError] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Phase IM — re-send confirmation modal state.
    const [resendTarget, setResendTarget] = useState(null);
    const [resending, setResending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // MGR1 ergonomics follow-up — admins + managers are stored under
            // separate role-segmented endpoints. Fan out, merge, and sort
            // (super_admin > admin > manager, then name asc) so newly-created
            // managers show up immediately in the unified list.
            const [adminsRes, managersRes] = await Promise.all([
                api.get("/admin/admins"),
                api.get("/admin/managers").catch(() => ({ data: [] })),
            ]);
            const ROLE_RANK = { super_admin: 0, admin: 1, manager: 2 };
            const merged = [...(adminsRes.data || []), ...(managersRes.data || [])];
            // Dedupe by id (defensive — endpoints are role-disjoint today).
            const byId = new Map();
            for (const u of merged) byId.set(u.id, u);
            const list = [...byId.values()].sort((a, b) => {
                const ra = ROLE_RANK[a.role] ?? 99;
                const rb = ROLE_RANK[b.role] ?? 99;
                if (ra !== rb) return ra - rb;
                return (a.full_name || "").localeCompare(b.full_name || "");
            });
            setAdmins(list);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onCreate = async (e) => {
        e.preventDefault();
        setFormError("");
        // Manager creation is super_admin-only on the backend; defense-in-depth here.
        if (form.role === "manager" && user?.role !== "super_admin") {
            setFormError("Only a super admin can create manager users");
            return;
        }
        setSubmitting(true);
        try {
            // MGR1 follow-up — route to /admin/managers when creating a yard
            // manager, /admin/admins (existing path) for regular admins.
            const endpoint = form.role === "manager" ? "/admin/managers" : "/admin/admins";
            const { email, full_name } = form;
            // Phase IM — no more temp_password; backend issues a magic link.
            const { data } = await api.post(endpoint, { email, full_name });
            const label = form.role === "manager" ? "Manager" : "Admin";
            if (data?.invite_sent === false) {
                toast.error(
                    data?.reason
                        ? `${label} created, but invite email failed: ${data.reason}. Use "Resend invite" once email is configured.`
                        : `${label} created, but invite email failed. Use "Resend invite" once email is configured.`
                );
            } else if (data?.mock_inbox) {
                toast.success(`${label} ${email} created — invite queued to Mock Emails inbox`);
            } else {
                toast.success(`${label} ${email} created — invite email sent`);
            }
            setForm({ email: "", full_name: "", role: "admin" });
            setCreateOpen(false);
            load();
        } catch (err) {
            setFormError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    // Phase IM — resend invite for a pending admin/manager.
    const onConfirmResend = async () => {
        if (!resendTarget) return;
        setResending(true);
        try {
            const path = resendTarget.role === "manager"
                ? `/admin/managers/${resendTarget.id}/resend-invite`
                : `/admin/admins/${resendTarget.id}/resend-invite`;
            const { data } = await api.post(path);
            if (data?.invite_sent === false) {
                toast.error(
                    data?.reason
                        ? `Invite email failed: ${data.reason}`
                        : "Invite email failed — check email provider settings."
                );
            } else if (data?.mock_inbox) {
                toast.success(`Invite re-queued to Mock Emails for ${resendTarget.email}`);
            } else {
                toast.success(`Invite re-sent to ${resendTarget.email}`);
            }
            setResendTarget(null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setResending(false);
        }
    };

    const onConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            // MGR1 ergonomics follow-up — managers live behind their own
            // delete endpoint. Route by role; admin/super_admin uses the
            // existing /admin/admins/{id} path.
            const path = deleteTarget.role === "manager"
                ? `/admin/managers/${deleteTarget.id}`
                : `/admin/admins/${deleteTarget.id}`;
            await api.delete(path);
            toast.success(`Removed ${deleteTarget.email}`);
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <DashboardShell>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                        Super admin · Team
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Admins
                    </h1>
                    <p className="mt-2 max-w-xl text-sm text-slate-600 leading-relaxed">
                        Manage who can access the operations dashboard. New invitees set their own password via the emailed link.
                    </p>
                </div>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="open-create-admin-dialog" className="bg-blue-900 hover:bg-blue-800 gap-2">
                            <Plus size={16} /> Add admin
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="create-admin-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-display text-xl">Add a new admin</DialogTitle>
                            <DialogDescription>
                                We'll email an invitation link so they can set their own password.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={onCreate} className="space-y-4 mt-2" data-testid="create-admin-form">
                            <div className="space-y-2">
                                <Label htmlFor="new_admin_full_name">Full name</Label>
                                <Input
                                    id="new_admin_full_name"
                                    data-testid="create-admin-fullname-input"
                                    required
                                    value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new_admin_email">Email</Label>
                                <Input
                                    id="new_admin_email"
                                    data-testid="create-admin-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new_admin_role">Role</Label>
                                <select
                                    id="new_admin_role"
                                    data-testid="create-admin-role-select"
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/20"
                                >
                                    <option value="admin" data-testid="role-option-admin">Admin</option>
                                    {user?.role === "super_admin" && (
                                        <option value="manager" data-testid="role-option-manager">Manager</option>
                                    )}
                                </select>
                                <p className="text-xs text-slate-500">
                                    {form.role === "manager"
                                        ? "Yard / field staff: mobile-only access to the inspection + return-record flow. Cannot view financials."
                                        : "Full access to operations dashboard, customers, rentals, and invoices."}
                                </p>
                            </div>
                            <div
                                data-testid="create-admin-invite-banner"
                                className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900 flex gap-2 items-start"
                            >
                                <Mail size={16} className="mt-0.5 flex-shrink-0" />
                                <span>
                                    An invitation email will be sent with a secure link to set their own password. The link expires in 7 days.
                                </span>
                            </div>
                            {formError && (
                                <div
                                    data-testid="create-admin-error"
                                    className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                                >
                                    {formError}
                                </div>
                            )}
                            <DialogFooter className="mt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCreateOpen(false)}
                                    data-testid="create-admin-cancel"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="create-admin-submit"
                                    className="bg-blue-900 hover:bg-blue-800"
                                >
                                    {submitting ? "Adding…" : (form.role === "manager" ? "Add manager" : "Add admin")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="mt-8">
                <CardContent className="p-0">
                    <Table data-testid="admins-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-10">
                                        Loading admins…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && admins.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-10">
                                        No admins yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                admins.map((a) => {
                                    const isSelf = a.id === user?.id;
                                    const isSuper = a.role === "super_admin";
                                    return (
                                        <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                                            <TableCell className="font-medium text-slate-900">
                                                <div className="flex items-center gap-2">
                                                    <UserPlus size={14} className="text-slate-400" />
                                                    {a.full_name}
                                                    {isSelf && (
                                                        <span className="text-xs font-normal text-slate-400">
                                                            (you)
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-slate-600">{a.email}</TableCell>
                                            <TableCell>
                                                <RoleBadge role={a.role} />
                                            </TableCell>
                                            <TableCell className="text-slate-500 text-sm">
                                                {a.must_reset_password ? "Invite pending" : "Active"}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {!isSelf && !isSuper && a.must_reset_password && user?.role === "super_admin" && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setResendTarget(a)}
                                                            data-testid={`resend-invite-${a.id}`}
                                                            className="text-blue-700 hover:text-blue-900 hover:bg-blue-50 gap-1.5"
                                                        >
                                                            <Send size={14} />
                                                            Resend invite
                                                        </Button>
                                                    )}
                                                    {!isSelf && !isSuper && (
                                                        <LogInAsButton
                                                            targetUserId={a.id}
                                                            targetEmail={a.email}
                                                            targetRole="admin"
                                                            dataTestid={`login-as-admin-${a.id}`}
                                                        />
                                                    )}
                                                    {user?.role === "super_admin" && (
                                                        <Button
                                                            asChild
                                                            variant="ghost"
                                                            size="sm"
                                                            data-testid={`sessions-link-${a.id}`}
                                                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 gap-1.5"
                                                        >
                                                            <Link to={`/admin/users/${a.id}/sessions`}>
                                                                <KeyRound size={14} />
                                                                Sessions
                                                            </Link>
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        disabled={isSelf || isSuper}
                                                        onClick={() => setDeleteTarget(a)}
                                                        data-testid={`delete-admin-${a.id}`}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 gap-1.5"
                                                    >
                                                        <Trash2 size={14} />
                                                        Remove
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-admin-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this admin?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget?.full_name} ({deleteTarget?.email}) will lose access to the dashboard. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-admin-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-admin-confirm"
                            disabled={deleting}
                            onClick={onConfirmDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? "Removing…" : "Remove admin"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Phase IM — re-send invite confirmation. Re-issuing a magic
                link invalidates any previously-sent link so a leaked URL
                can't be re-used. */}
            <AlertDialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
                <AlertDialogContent data-testid="resend-invite-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Re-send invitation email?</AlertDialogTitle>
                        <AlertDialogDescription>
                            We'll email a fresh invite link to <strong>{resendTarget?.email}</strong>. Any previously-sent link for this user will be invalidated.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="resend-invite-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="resend-invite-confirm"
                            disabled={resending}
                            onClick={onConfirmResend}
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            {resending ? "Sending…" : "Send invite"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardShell>
    );
}
